/**
 * SatelliteTileLayer.tsx — طبقة القمر الصناعي (مسار هجين)
 *
 * مساران يعملان معاً، لكل منهما ما لا يحسنه الآخر:
 *
 *  • **نسيج عالمي واحد** (`global.jpg`، 4096×2048) على طبقة GL مخصّصة — موجود **دائماً في
 *    الأسفل**. هو مصدر الانسيابية: نسيجان على الـGPU يُمزجان بكسر الزمن، فتتحرّك الغيوم
 *    بلا أي تقطيع مهما تسارع الشريط، وبطلب شبكة واحد لكل إطار بدل عشرات.
 *  • **هرم بلاطات XYZ** فوقه، يُضاف فقط عند التكبير أعمق من TILE_MIN_ZOOM — وهو مصدر
 *    الحدّة (2.4 كم/بكسل مقابل 9.8 للنسيج).
 *
 * لماذا يبقى النسيج في الأسفل بدل التبديل بينهما: الاثنان اشتقاق من **نفس** الموزاييك
 * (البلاطات والنسيج يخرجان من نفس سلسلة التصغير)، فيتطابقان لوناً وهندسةً تماماً. فالنسيج
 * يعمل تغطيةً دائمة: لا توجد لحظة واحدة تكون فيها الخريطة بلا صور — لا عند تحميل بلاطات
 * جديدة، ولا عند التفاف الشريط الزمني، ولا عند تبديل الزووم. البلاطات تضيف تفصيلاً فوق
 * صورة موجودة أصلاً، بدل أن تملأ ثقباً.
 *
 * الشريط الزمني: نختار الإطار المطابق للحظة المعروضة ونمزجه مع تاليه بكسر الزمن بينهما.
 * صور القمر ماضٍ فقط (تتأخّر 10-30 دقيقة)، فحين تُفعَّل هذه الطبقة يتحوّل الشريط كلّه إلى
 * محور السلسلة (ماضٍ فقط) بدل محور التوقّعات — كما في Zoom Earth. الفهرس يجلبه
 * NewWeatherMap لأنه يبني منه ذلك المحور.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { Map as MaplibreMap } from 'maplibre-gl';
import { useWeatherStore } from '../../store/weatherStore';
import { useMapRef } from './MapContext';
import { SatelliteRasterGLLayer } from './webgl/SatelliteRasterGLLayer';
import { getWeatherInsertBeforeId, raiseParticlesAboveWeatherLayers } from './webgl/layerOrder';
import type { SatelliteMeta } from './hooks/useSatelliteFrames';
import { queuePrefetch } from '../../services/prefetchQueue';
import { prefetchRasterBytes } from '../../services/rasterImageCache';

/**
 * الزووم الذي تبدأ عنده البلاطات — **صفر: في كل المستويات**.
 *
 * كان 3 (عرض النسيج 4096 = عرض العالم عند zoom 3، فبدا أن ما دونه لا يحتاج بلاطات).
 * كان ذلك خطأً في تقدير ما يهمّ: النسيج ملفّ واحد ~1.5MB **لا يظهر منه شيء حتى يكتمل**،
 * بينما البلاطة ~30KB وتصل عشراتُها تدريجياً. فكان المنظور العالمي — وهو أوّل ما يراه
 * الزائر، وأوسعُ ما يكون على الديسكتوب — هو الحالة الوحيدة المحرومة من العرض التدريجي،
 * فبدا التقريب أسرع من التبعيد. عند zoom 0-2 عدد البلاطات 1 و4 و16 فقط، فالكلفة ضئيلة
 * والمكسب هو أول عرض.
 *
 * النسيج يبقى تحتها لأنه لا يزال يحمل الحركة السلسة على الشريط (مزج GPU بطلب واحد).
 */
const TILE_MIN_ZOOM = 0;

const GL_LAYER_ID = 'satellite-global';
/**
 * ثلاث فتحات: الإطار المعروض، وتاليه، و**آخر إطار اكتملت بلاطاته**.
 *
 * كانت فتحتين، والنسيج العالمي وحده هو التغطية عند الانتقال. لكن النسيج 9.8 كم/بكسل
 * والبلاطات 2.4 — فكل خطوة على الشريط كانت تهدم بلاطات الإطار السابق فور تغيّر الرابط،
 * قبل أن تجهز بلاطات الجديد، فينكشف النسيج لثوانٍ: هذا بالضبط ما يُرى «جودة رديئة ثم
 * ممتازة» عند كل إطار. الفتحة الثالثة تُبقي آخر صورة حادّة معروضة تحت الإطار الجديد
 * حتى يكتمل تحميله (isSourceLoaded)، فتصير أسوأ حالة «صورة حادّة متأخّرة لحظةً» بدل
 * «صورة ضبابية». لا تكلّف بايتاً واحداً من الشبكة — الطبقة موجودة ومحمَّلة أصلاً.
 */
const SLOTS = ['a', 'b', 'c'] as const;
type Slot = (typeof SLOTS)[number];

const sourceId = (s: Slot) => `satellite-src-${s}`;
/** البادئة satellite- معروفة لـ layerOrder.isWeatherManagedLayer — لا تُغيَّر. */
const layerId = (s: Slot) => `satellite-layer-${s}`;

/**
 * يُحمّي رابطاً واحداً مرّة واحدة — نسيجاً عالمياً كان أو بلاطة.
 *
 * **بايتات فقط وعبر طابور التزامن المحدود**، لا `new Image()` مباشرةً:
 *  • البايتات كافية تماماً — من يرفع إلى الـGPU هو MapLibre (للبلاطات) أو
 *    SatelliteRasterGLLayer (للنسيج)، وكلاهما يفكّ بنفسه من كاش الشبكة. أمّا فكّها هنا
 *    فيملأ كاش الصور المفكوكة (سعته أربعون **صورة** لا بايتاً) بنُسج 33 ميغابايت،
 *    فيطرد إطارات الطقس المجاورة ويلتهم الذاكرة بلا مقابل.
 *  • والطابور هو ما يمنع فتح عشرات التنزيلات دفعةً واحدة (الاستضافة تقطع تدفّقات HTTP/2)،
 *    ويضمن أن ما يحتاجه العرض الآن (`urgent`) يسبق دائماً تعبئة بقيّة السلسلة.
 */
function warm(url: string, seen: Set<string>, priority: 'urgent' | 'background' = 'urgent') {
    if (!url || seen.has(url)) return;
    seen.add(url);
    queuePrefetch(() => prefetchRasterBytes(url), priority);
}

/**
 * فهارس البلاطات (z, x, y) التي يغطّيها منظور الخريطة الحالي عند مستوى الزووم الذي
 * سيطلبه MapLibre فعلياً — معادلة slippy-tile القياسية.
 *
 * مستوى z هنا تقريب (تقريب الزووم المستمرّ لأقرب صحيح، مقيَّداً بـmaxzoom) لا يطابق
 * اختيار MapLibre الداخلي حرفياً في كل الحالات (له قواعد overzoom/underzoom إضافية)،
 * لكن هذا غير حرج: هدف الدالّة تسخين ذاكرة المتصفّح احتياطاً، فتطابق تقريبي يكفي —
 * وأي بلاطة لم تُسخَّن ستُطلب من الشبكة كالمعتاد بلا أي كسر.
 */
function viewportTileIndices(map: MaplibreMap, maxzoom: number): Array<[number, number, number]> {
    const z = Math.max(0, Math.min(maxzoom, Math.round(map.getZoom())));
    const n = 2 ** z;
    const b = map.getBounds();
    const lonToX = (lon: number) => Math.floor(((lon + 180) / 360) * n);
    const latToY = (lat: number) => {
        const clamped = Math.max(-85.0511, Math.min(85.0511, lat));
        const rad = (clamped * Math.PI) / 180;
        return Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n);
    };
    const west = b.getWest();
    const east = b.getEast();
    const x0 = ((lonToX(west) % n) + n) % n;
    const x1 = ((lonToX(east) % n) + n) % n;
    const y0 = Math.max(0, Math.min(n - 1, latToY(b.getNorth())));
    const y1 = Math.max(0, Math.min(n - 1, latToY(b.getSouth())));
    // عدد الأعمدة ملفوفاً أفقياً (المنظور قد يعبر خطّ التاريخ أو نسخة عالم أخرى).
    // حالة حافّة: منظور يغطّي العالم كاملاً (~360°) يُرجع lonToX(east) نفس lonToX(west)
    // بعد الالتفاف (180° وَ−180° يقعان في نفس العمود) فيبدو عمود واحد لا الكرة كاملة —
    // نفحص الاتّساع الفعلي مباشرةً بدل الاستدلال من تساوي x0/x1.
    const xCount = east - west >= 359.9 ? n : (x1 >= x0 ? x1 - x0 + 1 : (n - x0) + x1 + 1);
    const CAP = 40; // سقف أمان — لا يمنع التسخين، فقط يوقفه عند منظور شاذّ الاتّساع
    const out: Array<[number, number, number]> = [];
    for (let y = y0; y <= y1 && out.length < CAP; y++) {
        for (let i = 0; i < xCount && out.length < CAP; i++) {
            out.push([z, (x0 + i) % n, y]);
        }
    }
    return out;
}

/** يسخّن ذاكرة المتصفّح ببلاطات المنظور الحالي لإطار مُعيَّن — بلا تسجيله كمصدر خريطة. */
function viewportTileUrls(map: MaplibreMap, tileUrlTemplate: string | null, maxzoom: number): string[] {
    if (!tileUrlTemplate) return [];
    return viewportTileIndices(map, maxzoom).map(([z, x, y]) =>
        tileUrlTemplate.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y)));
}

function prefetchViewportTiles(map: MaplibreMap, tileUrlTemplate: string | null, maxzoom: number, seen: Set<string>, priority: 'urgent' | 'background' = 'urgent') {
    for (const u of viewportTileUrls(map, tileUrlTemplate, maxzoom)) warm(u, seen, priority);
}

/**
 * تعبئة مخزن البلاطات لكامل الحلقة — **عبر الطابور الخلفي لا `requestIdleCallback`**.
 *
 * كانت الجدولة بـ`requestIdleCallback`، وهو لا يُطلَق إلا حين لا عمل معلّق. وأثناء
 * التشغيل لا يخمل المتصفّح أبداً (إعادة رسم مستمرّة)، فالمخزن الذي يُفترض أن يُبنى قبل
 * وصول الشريط لم يكن يُبنى إطلاقاً — وهو بالضبط الدرس الذي طُبِّق سابقاً على النسيج
 * العالمي ولم يُطبَّق على البلاطات. الطابور الخلفي يعمل فعلاً أثناء التشغيل، وبحدّ
 * تزامن يمنعه من مزاحمة الصفّ العاجل (الإطار الذي يُنتظر الآن).
 */
function queueBackgroundTiles(urls: string[], seen: Set<string>) {
    for (const u of urls) warm(u, seen, 'background');
}

interface Props {
    /** اللحظة المعروضة على الشريط الزمني (epoch بالثواني). */
    displayEpoch: number;
    /** فهرس الإطارات — يجلبه NewWeatherMap لأنه يبني منه محور الشريط أيضاً. */
    meta: SatelliteMeta | null;
}

export function SatelliteTileLayer({ displayEpoch, meta }: Props) {
    const { visibleLayers, isPlaying } = useWeatherStore(useShallow((s) => ({
        visibleLayers: s.visibleLayers,
        isPlaying: s.isPlaying,
    })));
    const mapRef = useMapRef();
    const glLayer = useRef<SatelliteRasterGLLayer | null>(null);
    /** روابط طُلب تحميلها مسبقاً — كي لا نكرّر إنشاء Image لنفس الرابط. */
    const prefetched = useRef(new Set<string>());
    /**
     * هل بدأت سلسلة تحميل الخمول؟ sync() تُستدعى كثيراً أثناء التشغيل (كل تغيّر إطار
     * ونبضة كل ثانية) — بلا هذا العلَم كانت كل استدعاءة تُطلق سلسلة جدولة مستقلّة، فتتراكم
     * عدّة سلاسل متوازية تُدرج نفس الروابط مراراً في طابور الإحماء بلا فائدة.
     * تُصفَّر عند تبديل الفهرس (إطارات جديدة من الخادم) فتُعاد جدولة ما تبقّى فقط.
     */
    const idleStarted = useRef<SatelliteMeta | null>(null);
    /**
     * بصمة (المنظور + طول السلسلة) التي اكتمل تخزينها المؤقّت. تمنع إعادة جدولة المخزن
     * الكامل في كل نبضة مزامنة، وتُعيد بناءه تلقائياً حين يتحرّك المستخدم أو يغيّر الزووم
     * (بلاطات المنظور الجديد مختلفة تماماً، فالمخزن القديم لم يعد يخدم شيئاً).
     */
    const bufferedView = useRef<string>('');
    /**
     * رابط بلاطات آخر إطار اكتمل تحميله داخل المنظور — هو ما تُبقيه الفتحة الثالثة
     * معروضاً ريثما تجهز بلاطات الإطار الجديد. يُصفَّر عند إخفاء الطبقة أو إعادة تركيبها.
     */
    const sharpUrl = useRef<string | null>(null);

    const base = `${import.meta.env.BASE_URL}rasters/`;

    /**
     * موضع اللحظة المعروضة داخل السلسلة: رقم الإطار السابق لها وكسر المسافة إلى تاليه.
     * الإطارات مرتّبة زمنياً تصاعدياً (يبنيها السكربت هكذا).
     */
    const frame = useMemo(() => {
        const frames = meta?.frames;
        if (!frames?.length) return null;
        if (displayEpoch <= frames[0].epoch) return { index: 0, blend: 0 };
        const last = frames.length - 1;
        if (displayEpoch >= frames[last].epoch) return { index: last, blend: 0 };
        let i = 0;
        while (i < last && frames[i + 1].epoch <= displayEpoch) i++;
        const span = frames[i + 1].epoch - frames[i].epoch;
        return { index: i, blend: span > 0 ? (displayEpoch - frames[i].epoch) / span : 0 };
    }, [meta, displayEpoch]);

    // أحدث فهرس/إطار عبر refs. **حرِج ضدّ سباق التوقيت:** إضافة الطبقة تتأخّر حتى جهوز
    // الستايل، بينما الفهرس يصل من الشبكة مستقلاً. لو وصل الفهرس أولاً لَما وجد طبقةً
    // ليضبطها، ولا يُعاد تشغيل مؤثّره لاحقاً — فتبقى الطبقة فارغة. لذا نطبّق أحدث القيم
    // داخل sync() نفسها عند الإضافة.
    const metaRef = useRef(meta); metaRef.current = meta;
    const frameRef = useRef(frame); frameRef.current = frame;

    const sync = (map: MaplibreMap) => {
        const m = metaRef.current;
        const f = frameRef.current;
        if (!m?.frames?.length || !f) return;

        const globalName = m.globalName ?? 'global.jpg';
        const globalUrl = (i: number) => {
            const fr = m.frames[i];
            return fr ? `${base}${fr.dir}/${globalName}` : '';
        };
        const tileUrl = (i: number) => {
            const fr = m.frames[i];
            return fr ? `${base}${fr.dir}/{z}/{x}/{y}.webp` : null;
        };

        // ── 1) النسيج العالمي: يحمل الحركة السلسة، وهو دائماً في الأسفل بلا شروط ──────
        const gl = ensureGlobalLayer(map, glLayer);
        // **لا شيء يُطلب قبل ظهور الإطار الحالي.** النسيج العالمي ~1.5MB، وطلبُ الإطار
        // التالي والتحميل المسبق فوراً كان يُطلق خمسة تنزيلات متزامنة (~9MB) قبل أن يظهر
        // أي شيء على الشاشة — فتتقاسم الوصلة بالتساوي ويصل الإطار الحالي خمس مرّات أبطأ.
        // هذا كان السبب المباشر لبطء أول عرض، وأوضحُه على الديسكتوب حيث المنظور أوسع.
        const firstReady = !!gl?.isReady();
        if (gl) {
            // الافتراض ±81° = الأفق الهندسي للقمر الجيوستاتيكي، لفهرس قديم بلا الحدّين.
            gl.setCoverage(m.lat_north ?? 81, m.lat_south ?? -81);
            gl.setFrames(globalUrl(f.index), firstReady ? globalUrl(f.index + 1) : '');
            gl.setBlend(f.blend);
        }
        // ── تحميل مسبق: نافذة صغيرة فوراً، والباقي في وقت خمول المتصفّح فقط ───────────
        // كان هذا يُطلق كل إطارات السلسلة دفعة واحدة (24-48 ملفّاً، ~35-70MB) فور ظهور
        // أول صورة. تلك الدفعة تحتلّ اتصالات المتصفّح، فحين يحتاج التشغيل بلاطات الإطار
        // **الحالي** فوراً تقف خلف ذلك الزحام في الطابور — تجمّد لحظي، ثم منطق التعويض في
        // useAutoplay يقفز عدّة خطوات دفعة واحدة ليلحق بالزمن الضائع، فتظهر النتيجة
        // كتقطيع يعقبه اندفاع سريع. هذا هو السبب الحقيقي، لا سرعة الإيقاع نفسها.
        //
        // الحل: نافذة صغيرة حول الإطار المعروض تُطلب فوراً (كافية لتشغيل سلس بلا توقّف)،
        // وبقيّة السلسلة تُطلب لاحقاً وعلى دفعات صغيرة **فقط حين يكون المتصفّح خاملاً**
        // (requestIdleCallback) — فلا تُنافس أبداً ما يحتاجه التشغيل الآن.
        if (firstReady && m.frames.length) {
            // نافذة فورية أوسع (كانت 3): مع تكرار الحلقة (loop) والانشغال المستمر بإعادة
            // الرسم أثناء التشغيل، requestIdleCallback نادراً ما يُطلَق — فالإطارات خلف
            // هذه النافذة كانت تبقى غير محمَّلة حتى يصل الشريط إليها فعلاً، فيظهر التقطّع
            // بعد أن تُستنفَد نافذة الثلاثة الأولى (وهي ما بدا "يوماً أول سلساً" ثم تعثّراً).
            const ahead = 8;
            for (let k = Math.max(0, f.index - 1); k <= Math.min(m.frames.length - 1, f.index + ahead); k++) {
                warm(globalUrl(k), prefetched.current, 'urgent');
            }
            // بقيّة السلسلة: عبر طابور التزامن المحدود (لا requestIdleCallback) كي يستمرّ
            // التحميل فعلياً أثناء التشغيل بدل التوقّف كلّما شغل الرسم الخيط الرئيسي.
            // مرّة واحدة لكل فهرس (لا لكل نبضة مزامنة) — راجع تعليق idleStarted أعلاه.
            if (idleStarted.current !== m) {
                idleStarted.current = m;
                for (let k = 0; k < m.frames.length; k++) {
                    // الصفّ الخلفي: لا يُنافس أبداً الإطار الذي ينتظره العرض الآن.
                    warm(globalUrl(k), prefetched.current, 'background');
                }
            }
        }

        // ── 2) البلاطات: **تخزين مؤقّت كامل قبل التشغيل، كمشغّل فيديو** ──────────────
        // هذا هو الفرق الجوهري عن Zoom Earth. كنّا نطلب بلاطات كل إطار لحظة الوصول إليه،
        // فنبقى رهينة الشبكة في كل خطوة — وأي تذبذب عابر يظهر كتقطّع. وZoom Earth يملأ
        // مخزناً مؤقّتاً لكامل الحلقة أولاً ثم يعرضها، فتصير كل خطوة قراءةً محلّية بحتة.
        //
        // نطبّق المبدأ نفسه: **نُسخّن بلاطات المنظور الحالي لكل إطارات السلسلة** (لا
        // الإطارين التاليين فقط). عددها = بلاطات المنظور (≤40) × عدد الإطارات، وحجمها
        // بضعة ميغابايتات لأن البلاطة ~30KB. تمرّ عبر الصفّ **الخلفي** من طابور الإحماء
        // (queueBackgroundTiles) فلا تزاحم أبداً ما يحتاجه العرض الآن، وبعدها تصبح كل
        // خطوة إصابةَ كاش (~1-5 مللي) بدل رحلة شبكة (~50-500 مللي).
        //
        // النافذة القريبة (الإطارات الثلاثة التالية) تُطلب فوراً لا بالخمول: هي ما يحتاجه
        // التشغيل في الثواني الأربع القادمة، فلا تحتمل الانتظار.
        //
        // النسيج العالمي يبقى الضامن دون قيد أو شرط: أي بلاطة لم تجهز بعد لا تُرسم أصلاً
        // (لا جزئياً ولا مكسورة)، والنسيج تحتها يواصل الحركة السلسة — تحسّن فوق أرضية
        // مضمونة، لا رهان عليها.
        //
        // ولا يُشترط `isPlaying`: بناء المخزن كان يبدأ عند أول ضغطة تشغيل فقط، فكانت
        // تلك الضغطة باردةً حتماً — أوّل إطارين أو ثلاثة بالنسيج وحده. الآن يبدأ فور
        // ظهور الطبقة، فحين يضغط المستخدم تشغيل يكون جزء معتبر من الحلقة جاهزاً محلياً.
        const mz = m.maxzoom ?? 5;
        for (let k = f.index; k <= f.index + 3; k++) {
            prefetchViewportTiles(map, tileUrl(k), mz, prefetched.current, 'urgent');
        }
        // المخزن الكامل: مرّة واحدة لكل (فهرس + منظور)، لا في كل نبضة مزامنة.
        const viewKey = `${m.frames.length}@${Math.round(map.getZoom())}:${map.getBounds().toArray().flat().map((v) => v.toFixed(2)).join(',')}`;
        if (bufferedView.current !== viewKey) {
            bufferedView.current = viewKey;
            const all: string[] = [];
            for (let k = 0; k < m.frames.length; k++) all.push(...viewportTileUrls(map, tileUrl(k), mz));
            queueBackgroundTiles(all, prefetched.current);
        }
        const tileZoomOk = map.getZoom() >= TILE_MIN_ZOOM;
        const curUrl = tileZoomOk ? tileUrl(f.index) : null;
        const nextUrl = tileZoomOk ? tileUrl(f.index + 1) : null;

        /**
         * رابط بلاطات الفتحة كما هو مسجَّل في الخريطة — null إن لم تكن مبنيّة بالكامل.
         *
         * **serialize() لا الحقل `tiles` مباشرةً.** الحقل العام `source.tiles` يملؤه
         * MapLibre بعد إطار رسم (يمرّ إعداد المصدر السطري عبر requestAnimationFrame)،
         * فقراءته فور الإضافة تُرجع undefined ⇒ تبدو الفتحة فارغة ⇒ نهدمها ونعيد بناءها
         * في كل نبضة ⇒ المصدر لا يُكمل تحميله أبداً ولا تُطلب أي بلاطة. أمّا serialize()
         * فيعيد الخيارات التي مرّرناها نحن، وهي متاحة فوراً بلا انتظار أي إطار.
         *
         * والقراءة من الخريطة لا من ref: المعرّفات عامّة بينما الـref خاصّ بنسخة المكوّن،
         * فإعادة تركيب المكوّن (وضع React الصارم يفعلها في التطوير) تجعل النسخة القديمة
         * تحذف طبقة أضافتها الجديدة ويبقى سجلّ الجديد يظنّها قائمة إلى الأبد.
         */
        const urlIn = (s: Slot): string | null => {
            if (!map.getLayer(layerId(s))) return null;
            const src = map.getSource(sourceId(s)) as { serialize?: () => { tiles?: string[] } } | undefined;
            return src?.serialize?.().tiles?.[0] ?? null;
        };

        const state: Record<Slot, string | null> = { a: urlIn('a'), b: urlIn('b'), c: urlIn('c') };
        const slotWith = (url: string | null) => (url ? SLOTS.find((s) => state[s] === url) : undefined);
        /** هل اكتملت بلاطات هذا الرابط داخل المنظور الحالي؟ */
        const isLoaded = (url: string | null) => {
            const s = slotWith(url);
            if (!s) return false;
            try { return map.isSourceLoaded(sourceId(s)); } catch { return false; }
        };

        // ── الإبقاء على آخر إطار حادّ ────────────────────────────────────────────────
        // ما دامت بلاطات الإطار الحالي لم تكتمل، نحتفظ بطبقة آخر إطار حادّ تحته بدل
        // هدمها. الحالي فوقها يرسم ما وصل منه فقط، وما لم يصل تسدّه الصورة الحادّة
        // السابقة (أقدم بدقائق، لكنها بنفس الحدّة) — لا النسيج الضبابي.
        const curLoaded = isLoaded(curUrl);
        if (curUrl && curLoaded) sharpUrl.current = curUrl;
        const kept = sharpUrl.current;
        const holdUrl = (curUrl && !curLoaded && kept && kept !== curUrl && kept !== nextUrl && slotWith(kept))
            ? kept
            : null;

        const want: (string | null)[] = [curUrl, nextUrl, holdUrl];
        for (const s of SLOTS) {
            if (state[s] && !want.includes(state[s])) { removeSlot(map, s); state[s] = null; }
            else if (!state[s] && map.getSource(sourceId(s))) { removeSlot(map, s); }
        }
        const slotOf: Record<string, Slot> = {};
        for (const s of SLOTS) if (state[s]) slotOf[state[s]!] = s;
        for (const url of want) {
            if (!url || slotOf[url]) continue;
            const free = SLOTS.find((s) => !state[s]);
            if (!free) continue;
            addSlot(map, free, url, m);
            state[free] = url;
            slotOf[url] = free;
        }

        // ── 3) الترتيب والشفافية ─────────────────────────────────────────────────────
        const sCur = curUrl ? slotOf[curUrl] : undefined;
        const sNext = nextUrl ? slotOf[nextUrl] : undefined;
        const sHold = holdUrl ? slotOf[holdUrl] : undefined;
        // من الأسفل إلى الأعلى: النسيج العالمي، ثم الإطار المحفوظ، ثم الحالي، ثم التالي.
        // (moveLayer(A, B) يضع A **تحت** B.) النسيج يبقى في القاع بلا شرط: هو التغطية
        // التي تمنع أي ثقب حين لا يكون أي من الثلاثة جاهزاً بعد.
        const stack = [sHold, sCur, sNext].filter((s): s is Slot => !!s && !!map.getLayer(layerId(s)));
        if (stack.length && map.getLayer(GL_LAYER_ID)) {
            try { map.moveLayer(GL_LAYER_ID, layerId(stack[0])); } catch { /* تجاهل */ }
        }
        for (let i = 0; i + 1 < stack.length; i++) {
            try { map.moveLayer(layerId(stack[i]), layerId(stack[i + 1])); } catch { /* تجاهل */ }
        }
        const setOp = (s: Slot | undefined, v: number) => {
            if (!s || !map.getLayer(layerId(s))) return;
            try { map.setPaintProperty(layerId(s), 'raster-opacity', v); } catch { /* تجاهل */ }
        };
        setOp(sHold, 1);
        setOp(sCur, 1);
        setOp(sNext, sCur ? f.blend : 1);
    };
    const syncRef = useRef(sync); syncRef.current = sync;

    /**
     * هل الستايل جاهز لاستقبال addSource/addLayer؟
     *
     * **ليس** `isStyleLoaded()` — هذه تعني «لا مصدر ولا خطّ قيد التحميل»، وهي تبقى false
     * إلى الأبد إن فشل أيّ طلب خطوط في ستايل الأساس (مرصود مع خطوط CARTO). الجاهزية
     * الحقيقية لإضافة طبقة هي أن الستايل قد **حُلِّل**، ودليلها وجود قائمة طبقاته.
     */
    const styleParsed = (map: MaplibreMap) => !!map.getStyle()?.layers?.length;
    const ready = useRef(false);

    useEffect(() => {
        if (!visibleLayers.satellite) return;
        const wrap = mapRef.current;
        if (!wrap) return;
        const map = wrap.getMap();
        let cancelled = false;
        ready.current = false;
        prefetched.current.clear();
        sharpUrl.current = null;

        const add = () => {
            if (cancelled || !styleParsed(map)) return false;
            try {
                ready.current = true;
                syncRef.current(map);
                raiseParticlesAboveWeatherLayers(map);
            } catch (e) { console.error('SatelliteTileLayer add', e); }
            return true;
        };
        // المستمعات تبقى **طوال عمر الطبقة** لا حتى أول إضافة فقط:
        //  • 'styledata' — إعادة تحميل الستايل (تبديل ثيم/قاعدة) تمحو الطبقات المضافة.
        //  • 'sourcedata' — جهوز بلاطات إطار جديد لا يُصدر 'styledata'.
        //  • 'zoomend' — عبور عتبة البلاطات يضيفها أو يزيلها.
        //  • النبضة الدورية — حذف من خارج دورة الأحداث (تنظيف نسخة قديمة من المكوّن) لا
        //    يُصدر أي حدث، فبلا نبضة تختفي الطبقة نهائياً حتى إعادة تحميل الصفحة.
        // النبضة رخيصة: sync على حالة سليمة = بضعة getLayer وضبط شفافية.
        const onStyleData = () => { add(); };
        const onSourceData = (e: { sourceId?: string; isSourceLoaded?: boolean }) => {
            if (e.sourceId?.startsWith('satellite-src-') && e.isSourceLoaded) add();
        };
        const onZoom = () => { add(); };
        map.on('styledata', onStyleData);
        map.on('sourcedata', onSourceData as never);
        map.on('zoomend', onZoom);
        const intervalId = window.setInterval(add, 1000);
        add();

        return () => {
            cancelled = true; ready.current = false;
            map.off('styledata', onStyleData);
            map.off('sourcedata', onSourceData as never);
            map.off('zoomend', onZoom);
            window.clearInterval(intervalId);
            for (const s of SLOTS) removeSlot(map, s);
            try { if (map.getLayer(GL_LAYER_ID)) map.removeLayer(GL_LAYER_ID); } catch { /* تجاهل */ }
            glLayer.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleLayers.satellite, mapRef]);

    // تبديل الإطارين ونسبة المزج — بلا إعادة بناء المصادر ما دام الرابط نفسه.
    // isPlaying ضمن الاعتماديات: الضغط على تشغيل/إيقاف يستحقّ مزامنة فورية (إعادة ترتيب
    // الفتحات وإحماء النافذة القريبة) بدل انتظار نبضة المراقب التالية (حتى ثانية كاملة).
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map || !visibleLayers.satellite || !ready.current) return;
        try { syncRef.current(map); } catch (e) { console.error('SatelliteTileLayer sync', e); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta, frame, visibleLayers.satellite, isPlaying]);

    return null;
}

/** يضيف طبقة النسيج العالمي إن لم تكن موجودة، ويعيدها. */
function ensureGlobalLayer(map: MaplibreMap, ref: React.MutableRefObject<SatelliteRasterGLLayer | null>) {
    if (map.getLayer(GL_LAYER_ID)) return ref.current;
    // الطبقة المخصّصة لا تظهر في getStyle().layers، فالمرجع هنا هو ما يمسكها — لكن
    // getLayer أعلاه يبقى المرجع الحقيقي لوجودها على الخريطة (قد تُمحى بإعادة الستايل).
    const layer = new SatelliteRasterGLLayer(GL_LAYER_ID, '', 1);
    try {
        map.addLayer(layer, getWeatherInsertBeforeId(map));
        ref.current = layer;
    } catch (e) {
        console.error('SatelliteTileLayer global', e);
        return null;
    }
    return ref.current;
}

function addSlot(map: MaplibreMap, slot: Slot, tiles: string, meta: SatelliteMeta) {
    if (map.getLayer(layerId(slot))) return;
    if (!map.getSource(sourceId(slot))) {
        map.addSource(sourceId(slot), {
            type: 'raster',
            tiles: [tiles],
            tileSize: meta.tileSize ?? 512,
            minzoom: meta.minzoom ?? 0,
            // maxzoom = أعلى مستوى **موجود فعلاً**؛ ما فوقه يُكبّر MapLibre نفس البلاطة
            // بدل طلب 404، فيبقى التكبير العميق حادّاً حتى maxzoom ثم يتدرّج بنعومة.
            maxzoom: meta.maxzoom ?? 5,
            attribution: 'NOAA/NESDIS/CIRA, JMA, EUMETSAT',
        } as never);
    }
    map.addLayer({
        id: layerId(slot),
        type: 'raster',
        source: sourceId(slot),
        // لا نطلب بلاطات تحت عتبة النسيج: هناك النسيج أحدّ منها أصلاً وأرخص.
        minzoom: TILE_MIN_ZOOM,
        paint: {
            'raster-opacity': 0,
            // صفر عمداً: مع إطارين متراكبين، تلاشي MapLibre بين مستويات الزووم يجعل
            // بلاطات الطبقة العليا نصف شفّافة أثناء التلاشي فيطلّ الإطار الآخر من تحتها
            // — يُقرأ كارتعاش لا كنعومة. المزج الزمني عندنا يتكفّل بالانسياب أصلاً.
            'raster-fade-duration': 0,
            'raster-resampling': 'linear',
        },
    } as never, getWeatherInsertBeforeId(map));
}

function removeSlot(map: MaplibreMap, slot: Slot) {
    try { if (map.getLayer(layerId(slot))) map.removeLayer(layerId(slot)); } catch { /* تجاهل */ }
    try { if (map.getSource(sourceId(slot))) map.removeSource(sourceId(slot)); } catch { /* تجاهل */ }
}
